import { FunctionComponent, ReactNode } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
	LeadRelationshipActiveCampaign,
	LeadRelationshipConnection,
	LeadRelationshipOtherList,
	LeadRelationshipPastOutreach,
	LeadReplySentiment,
} from "@signalist/shared-types";

import { LeadLabelColor, SEGMENT_COLORS } from "./LeadColorLabels";
import { formatRelationshipDate, getInitials, SENTIMENT_CHIP } from "./leadRelationshipFormat";

/** A reply-sentiment pill (Interested / Bad Timing / …). */
export const ReplySentimentChip: FunctionComponent<{ sentiment: LeadReplySentiment }> = ({
	sentiment,
}) => {
	const { t } = useTranslation();
	const { classes, i18nKey } = SENTIMENT_CHIP[sentiment];

	return (
		<span
			className={`inline-flex items-center h-[21px] px-2 rounded-md text-xs font-semibold whitespace-nowrap ${classes}`}
		>
			{t(i18nKey)}
		</span>
	);
};

/** Shared section header: color dot + title + optional count badge. */
export const SectionHeader: FunctionComponent<{
	color: LeadLabelColor;
	title: string;
	count?: ReactNode;
}> = ({ color, title, count }) => (
	<div className="flex items-center gap-2 px-3.5 pt-3 pb-2">
		<span className={`w-2 h-2 rounded-full shrink-0 ${SEGMENT_COLORS[color]}`} />
		<span className="text-xs font-bold uppercase tracking-wide text-text-meta">{title}</span>
		{count != null && (
			<span className="ml-auto text-xxs font-semibold text-text-meta bg-gray-100 px-2 py-0.5 rounded-full">
				{count}
			</span>
		)}
	</div>
);

export const ActiveCampaignSection: FunctionComponent<{
	data: LeadRelationshipActiveCampaign;
}> = ({ data }) => {
	const { t } = useTranslation();
	const assigned = formatRelationshipDate(data.assignedAt);

	const meta = [
		assigned ? t("inbox.relationshipHover.assignedOn", { date: assigned }) : null,
		data.stepLabel,
	]
		.filter(Boolean)
		.join(" · ");

	return (
		<>
			<SectionHeader color="red" title={t("inbox.relationshipHover.activeCampaign")} />
			<div className="px-3.5 pb-3">
				<div className="text-sm font-semibold text-text">{data.campaignName}</div>
				{meta && <div className="text-xs text-text-meta mt-0.5">{meta}</div>}
			</div>
		</>
	);
};

export const PastOutreachSection: FunctionComponent<{
	rows: LeadRelationshipPastOutreach[];
}> = ({ rows }) => {
	const { t } = useTranslation();

	return (
		<>
			<SectionHeader
				color="yellow"
				title={t("inbox.relationshipHover.pastOutreach")}
				count={t("inbox.relationshipHover.campaignCount", { count: rows.length })}
			/>
			<div className="flex flex-col">
				{rows.map((row) => {
					const assigned = formatRelationshipDate(row.assignedAt);

					return (
						<div
							key={`past-${row.campaignName}-${row.assignedAt ?? ""}`}
							className="flex items-center gap-2 px-3.5 py-2"
						>
							<div className="flex-1 min-w-0">
								<div className="text-sm font-semibold text-text truncate">{row.campaignName}</div>
								{assigned && (
									<div className="text-xs text-text-meta mt-0.5">
										{t("inbox.relationshipHover.assignedOn", { date: assigned })}
									</div>
								)}
								<div className="flex items-center gap-1.5 mt-1">
									{row.firstSentiment && <ReplySentimentChip sentiment={row.firstSentiment} />}
									{row.lastSentiment && row.lastSentiment !== row.firstSentiment && (
										<ReplySentimentChip sentiment={row.lastSentiment} />
									)}
								</div>
							</div>
							{row.ended && (
								<span className="shrink-0 text-xxs font-semibold text-text-meta bg-gray-100 px-2 py-0.5 rounded-md">
									{t("inbox.relationshipHover.ended")}
								</span>
							)}
						</div>
					);
				})}
			</div>
		</>
	);
};

export const ConnectionsSection: FunctionComponent<{
	rows: LeadRelationshipConnection[];
}> = ({ rows }) => {
	const { t } = useTranslation();

	return (
		<>
			<SectionHeader
				color="blue"
				title={t("inbox.relationshipHover.connectedOnLinkedin")}
				count={t("inbox.relationshipHover.peopleCount", { count: rows.length })}
			/>
			<div className="flex flex-col">
				{rows.map((row) => {
					const connected = formatRelationshipDate(row.connectedAt);

					return (
						<div
							key={`conn-${row.name}-${row.connectedAt ?? ""}`}
							className="flex items-center gap-2.5 px-3.5 py-1.5"
						>
							<div className="w-6 h-6 shrink-0 rounded-full bg-blue-action text-white text-xxs font-bold flex items-center justify-center">
								{getInitials(row.name)}
							</div>
							<div className="min-w-0">
								<div className="text-sm font-semibold text-text truncate">{row.name}</div>
								{connected && (
									<div className="text-xs text-text-meta">
										{t("inbox.relationshipHover.connectedOn", { date: connected })}
									</div>
								)}
							</div>
						</div>
					);
				})}
			</div>
		</>
	);
};

export const OtherListsSection: FunctionComponent<{
	rows: LeadRelationshipOtherList[];
}> = ({ rows }) => {
	const { t } = useTranslation();

	return (
		<>
			<SectionHeader
				color="grey"
				title={t("inbox.relationshipHover.onOtherLists")}
				count={t("inbox.relationshipHover.listCount", { count: rows.length })}
			/>
			<div className="flex flex-col">
				{rows.map((row) => {
					const added = formatRelationshipDate(row.addedAt);

					return (
						<Link
							key={`list-${row.listId}`}
							to={`/app/lists/detail/${row.listId}`}
							target="_blank"
							className="flex items-center gap-2 px-3.5 py-2 hover:bg-brand-white"
						>
							<div className="flex-1 min-w-0">
								<div className="text-sm font-semibold text-text truncate hover:underline">
									{row.listName}
								</div>
								{added && <div className="text-xs text-text-meta mt-0.5">{added}</div>}
							</div>
							<span className="shrink-0 text-xxs font-semibold text-text-meta bg-gray-100 px-2 py-0.5 rounded-md">
								{t(`inbox.assignStatus.${row.assignStatus}`)}
							</span>
						</Link>
					);
				})}
			</div>
		</>
	);
};
